import PromotionalRibbon from "@/components/PromotionalRibbon";
import { Image } from "@/components/image";
import { Text } from "@/components/ui";
import { NoImage } from "@/components/no-image/NoImage";
import {
  CATEGORY_CARD_HEIGHT_REM,
  STOREFRONT_ROUNDED_PX,
} from "@/domain/storefront-appearance";
import { useStorefrontAppearance } from "@/hooks/use-storefront-appearance";
import { Category, ItemOffer } from "@/types";
import { useMemo } from "react";

/**
 * Category card of the CATEGORY_LIST grid, shared by the delivery home and the
 * table/QR menu. Wrappers choose the offers source (delivery vs indoor atoms)
 * and the click destination; height, corner and description come from
 * `appearance.categoryList`.
 */
export function CategoryImageCard({
  category,
  categoryItemOffers,
  onClick,
}: {
  category: Category;
  /** Offers of this category, used to derive the promotional ribbon. */
  categoryItemOffers: ItemOffer[];
  onClick: () => void;
}) {
  const hasPromotion = useMemo(
    () =>
      categoryItemOffers.some(
        (io) =>
          io.isPromotional ||
          (io.price.originalValue && io.price.originalValue > io.price.value),
      ),
    [categoryItemOffers],
  );

  const { categoryList } = useStorefrontAppearance();
  const showPromotionalRibbon = category.isPromotional || hasPromotion;
  const promotionalText =
    category.promotionalBannerText || (hasPromotion ? "PROMO" : undefined);
  const description = categoryList.showDescription
    ? category.description
    : undefined;

  return (
    <div className="relative">
      <div
        onClick={onClick}
        data-testid="category-image-card"
        style={{
          borderRadius: `${STOREFRONT_ROUNDED_PX[categoryList.rounded]}px`,
          height: `${CATEGORY_CARD_HEIGHT_REM[categoryList.cardSize]}rem`,
        }}
        className="relative block overflow-hidden group cursor-pointer shadow-card"
      >
        <PromotionalRibbon
          isPromotional={showPromotionalRibbon}
          text={promotionalText}
        />
        {category.image?.URL ? (
          <Image
            width={500}
            height={500}
            className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
            alt={category.name}
            src={category.image.URL}
            crc={category.image["CRC-32"]}
            sizes="(min-width: 768px) 460px, 90vw"
          />
        ) : (
          <NoImage width={500} height={500} className="h-full w-full object-cover" />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-scrim/90 via-scrim/40 to-transparent z-raised" />

        <div className="absolute bottom-0 left-0 right-0 p-4 pb-3 z-raised-2">
          <Text size="3xl" weight="extrabold" leading="tight" tone="on-media">
            {category.name}
          </Text>
          {description && (
            <Text size="base" weight="bold" truncate tone="warning-on-media">
              {description}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
